export type UserRole =
  | 'user'
  | 'tester'
  | 'project_member'
  | 'enterprise_admin'
  | 'dx_editor'
  | 'darwinx_admin';

export interface RoleUser {
  role: UserRole;
}

export const USER_ROLES: UserRole[] = [
  'user',
  'tester',
  'project_member',
  'enterprise_admin',
  'dx_editor',
  'darwinx_admin',
];

export const ROLE_LABELS: Record<UserRole, string> = {
  user: 'Joueur',
  tester: 'Testeur',
  project_member: 'Membre projet',
  enterprise_admin: 'Admin client',
  dx_editor: 'DX Editeur',
  darwinx_admin: 'DX Admin',
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  user: 'Accès aux histoires de son workspace. Peut laisser un commentaire pendant le jeu.',
  tester: 'Comme Joueur, avec en plus le menu développeur (Ctrl+K) en mode jeu.',
  project_member: 'Comme Testeur, avec accès au dashboard analytics de son workspace.',
  enterprise_admin:
    'Administration de son workspace : analytics, activation des comptes et gestion du service des utilisateurs.',
  dx_editor:
    'Outils d’édition DarwinX (Scenario Studio, Story Admin, Reviews) et accès aux scénarios non publiés.',
  darwinx_admin:
    'Accès complet : Master Dashboard, gestion des workspaces, attribution des rôles et configuration globale.',
};

type Permission =
  | 'submitReview'
  | 'accessGameDeveloperMenu'
  | 'viewAnalytics'
  | 'manageEnterpriseUserDepartment'
  | 'activateEnterpriseUsers'
  | 'viewReviewsAdmin'
  | 'accessScenarioTools'
  | 'accessMasterDashboard'
  | 'assignRoles'
  | 'seeUnreleasedStories';

const PERMISSIONS: Record<Permission, UserRole[]> = {
  submitReview: ['user', 'tester', 'project_member', 'enterprise_admin', 'dx_editor', 'darwinx_admin'],
  accessGameDeveloperMenu: ['tester', 'project_member', 'enterprise_admin', 'dx_editor', 'darwinx_admin'],
  viewAnalytics: ['project_member', 'enterprise_admin', 'dx_editor', 'darwinx_admin'],
  manageEnterpriseUserDepartment: ['enterprise_admin', 'darwinx_admin'],
  activateEnterpriseUsers: ['enterprise_admin', 'darwinx_admin'],
  viewReviewsAdmin: ['dx_editor', 'darwinx_admin'],
  accessScenarioTools: ['dx_editor', 'darwinx_admin'],
  accessMasterDashboard: ['darwinx_admin'],
  assignRoles: ['darwinx_admin'],
  seeUnreleasedStories: ['dx_editor', 'darwinx_admin'],
};

function hasPermission(role: UserRole | undefined, permission: Permission): boolean {
  if (!role) return false;
  return PERMISSIONS[permission].includes(role);
}

export function getRoleLabel(role: string): string {
  return ROLE_LABELS[role as UserRole] ?? 'Joueur';
}

export function isDarwinXAdmin(user: RoleUser | null | undefined): boolean {
  return user?.role === 'darwinx_admin';
}

export function isEnterpriseAdmin(user: RoleUser | null | undefined): boolean {
  return user?.role === 'enterprise_admin';
}

export function isDxEditor(user: RoleUser | null | undefined): boolean {
  return user?.role === 'dx_editor';
}

export function canSubmitReview(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'submitReview');
}

export function canAccessGameDeveloperMenu(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'accessGameDeveloperMenu');
}

export function canAccessAnalytics(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'viewAnalytics');
}

export function canManageEnterpriseUsers(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'manageEnterpriseUserDepartment');
}

export function canActivateEnterpriseUsers(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'activateEnterpriseUsers');
}

export function canAccessScenarioTools(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'accessScenarioTools');
}

export function canAccessReviewsDashboard(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'viewReviewsAdmin');
}

export function canAccessMasterDashboard(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'accessMasterDashboard');
}

export function canAssignRoles(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'assignRoles');
}

export function canSeeUnreleasedStories(user: RoleUser | null | undefined): boolean {
  return hasPermission(user?.role, 'seeUnreleasedStories');
}

/** @deprecated Use canAccessAnalytics */
export function canAccessEnterpriseDashboard(user: RoleUser | null | undefined): boolean {
  return canAccessAnalytics(user);
}

/** @deprecated Use canAccessMasterDashboard */
export function canAccessDarwinXDashboard(user: RoleUser | null | undefined): boolean {
  return canAccessMasterDashboard(user);
}

/** @deprecated Use canAccessScenarioTools */
export function canAccessStoryAdmin(user: RoleUser | null | undefined): boolean {
  return canAccessScenarioTools(user);
}

/** @deprecated Use canAccessReviewsDashboard */
export function canAccessReviews(user: RoleUser | null | undefined): boolean {
  return canAccessReviewsDashboard(user);
}
